var _excluded = ["id", "onMouseEnter", "onMouseMove", "onMouseLeave", "onClick", "isFocusable"];

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

/* eslint-disable max-len */
import { useClickable } from '@chakra-ui/clickable';
import { createDescendantContext } from '@chakra-ui/descendant';
import { useFormControl } from '@chakra-ui/form-control';
import { useControllableState, useDisclosure, useIds, useOutsideClick, useShortcut, useUpdateEffect } from '@chakra-ui/hooks';
import { usePopper } from '@chakra-ui/popper';
import { createContext, mergeRefs } from '@chakra-ui/react-utils';
import { ariaAttr, callAllHandlers, dataAttr, determineLazyBehavior, focus, getNextItemFromSearch, normalizeEventKey } from '@chakra-ui/utils';
import { useCallback, useRef, useState } from 'react';
import { findOption, stripDiacritics } from './utils';
/* -------------------------------------------------------------------------------------------------
 * Create context to track descendants and their indices
 * -----------------------------------------------------------------------------------------------*/

export var [SelectDescendantsProvider, useSelectDescendantsContext, useSelectDescendants, useSelectDescendant] = createDescendantContext();
/* -------------------------------------------------------------------------------------------------
 * Create context to track menu state and logic
 * -----------------------------------------------------------------------------------------------*/

export var [SelectProvider, useSelectContext] = createContext({
  strict: true,
  name: 'SelectContext'
});

/**
 * React Hook to manage a Select
 *
 * It provides the logic and will be used with react context
 * to propagate its return value to all children
 */
export function useSelect(props) {
  var _selectRef$current;

  var {
    closeOnSelect = true,
    defaultValue,
    id = 'select',
    isClearable = true,
    isLazy,
    isDisabled,
    leftIcon,
    lazyBehavior = 'unmount',
    onChange: onChangeProp,
    options = [],
    placeholder = 'Search...',
    placement = 'bottom-start',
    isSearchable,
    rightIcon,
    value: valueProp
  } = props;
  var {
    isOpen,
    onOpen,
    onClose,
    onToggle
  } = useDisclosure(props);
  /**
   * Prepare the reference to the Select and disclosure
   */

  var selectRef = useRef(null);
  var valueRef = useRef(null);
  var inputRef = useRef(null);
  /**
   * Enable the Select controlled and uncontrolled scenarios
   * Context to update the selected time value
   */

  var [value, setValue] = useControllableState({
    defaultValue: defaultValue,
    value: valueProp,
    onChange: onChangeProp
  });
  /** Input Value for search/filter */

  var [inputValue, setInputValue] = useState('');
  /** Input placeholder */

  var getPlaceholder = useCallback(() => {
    if (value) return findOption(value, options);
    if (defaultValue) return findOption(defaultValue, options);
    return placeholder;
  }, [defaultValue, options, placeholder, value]);
  var placeholderProps = getPlaceholder();
  /**
   * filtered options for SelectList when isEditing
   */

  var [filterOptions, setFilterOptions] = useState([]);
  /**
   * Generate unique ids for Select's list and input/value
   */

  var [valueId, listId] = useIds(id, 'value', 'list');
  var {
    'aria-invalid': isInvalid
  } = useFormControl(props);
  useOutsideClick({
    ref: selectRef,
    handler: event => {
      var _inputRef$current;

      if (isOpen && !((_inputRef$current = inputRef.current) != null && _inputRef$current.contains(event.target))) {
        onClose();
      }
    }
  });
  /**
   * Add some popper.js for dynamic positioning
   */

  var popper = usePopper(_extends({}, props, {
    placement,
    offset: [0, 0]
  }));
  /**
   * sets the List width by the inputRef's clientWidth
   * the list component needs a calculated value 
   */

  var [listWidth, setListWidth] = useState(selectRef == null ? void 0 : (_selectRef$current = selectRef.current) == null ? void 0 : _selectRef$current.clientWidth);
  /**
   * Context to register all SelectOption and SelectInput nodes
   */

  var descendants = useSelectDescendants();
  /**
   * Register the SelectInput's node into the domContext
   */

  var [focusedIndex, setFocusedIndex] = useState(0);
  var openAndFocusFirstOption = useCallback(() => {
    onOpen();
    setFocusedIndex(1);
  }, [onOpen, setFocusedIndex]);
  var openAndFocusLastItem = useCallback(() => {
    onOpen();
    setFocusedIndex(options.length - 1);
  }, [onOpen, setFocusedIndex, options]);
  /**
   * callback for list motion animation on completion
   */

  var motionCallback = useCallback(() => {
    setInputValue('');
  }, [setInputValue]);
  var inputOnChange = useCallback(searchedValue => {
    setInputValue(searchedValue);
    var filterOptions = options.filter(option => {
      var matchesVal = option.value.toLowerCase().includes(searchedValue);
      var matchesLabel = option.label.toLowerCase().includes(searchedValue);
      return matchesVal || matchesLabel;
    });
    setFilterOptions(filterOptions);
  }, [options, setInputValue, setFilterOptions]);
  /**  set the input width */

  useUpdateEffect(() => {
    var _selectRef$current2;

    setListWidth(selectRef == null ? void 0 : (_selectRef$current2 = selectRef.current) == null ? void 0 : _selectRef$current2.clientWidth);

    if (!isOpen) {
      setFocusedIndex(-1);
    }

    if (isOpen && isSearchable) {
      var _inputRef$current2;

      return (_inputRef$current2 = inputRef.current) == null ? void 0 : _inputRef$current2.focus();
    }

    if (isOpen && !isSearchable) {
      return setFocusedIndex(1);
    }
  }, [isOpen]);
  return {
    closeOnSelect,
    defaultValue,
    descendants,
    filterOptions,
    focusedIndex,
    forceUpdate: popper.forceUpdate,
    setValue,
    isClearable,
    inputOnChange,
    inputRef,
    inputValue,
    isDisabled,
    isInvalid,
    isLazy,
    isOpen,
    isSearchable,
    lazyBehavior,
    leftIcon,
    onClose,
    onOpen,
    onToggle,
    onTransitionEnd: motionCallback,
    openAndFocusFirstOption,
    openAndFocusLastItem,
    options,
    placeholderProps,
    popper,
    listId,
    selectRef,
    setFocusedIndex,
    setInputValue,
    rightIcon,
    value,
    valueId,
    valueRef,
    listWidth
  };
}

function isTargetOptionItem(target) {
  var _target$getAttribute;

  // this will catch `option`
  return target instanceof HTMLElement && !!((_target$getAttribute = target.getAttribute('role')) != null && _target$getAttribute.startsWith('option'));
}
/**
 * React Hook to manage a SelectValueContainer, SelectValue, & SelectInput.
 *
 * The assumption here is that the `useSelect` hook is used
 * in a component higher up the tree, and its return value
 * is passed as `context` to this hook.
 * This context will hold the functionality needed only for the Input and it's container
 */


export function useSelectValueContainer(props, externalRef) {
  if (externalRef === void 0) {
    externalRef = null;
  }

  var Select = useSelectContext();
  var {
    popper,
    valueId,
    setFocusedIndex,
    isClearable,
    isSearchable,
    isDisabled,
    onClose,
    onOpen,
    leftIcon,
    isOpen,
    isInvalid,
    rightIcon,
    setValue,
    setInputValue,
    value
  } = Select;

  if (!Select) {
    throw new Error('useSelectContext: context is undefined. Seems you forgot to wrap component within <Select>');
  }

  var onContainerClick = useCallback(() => {
    if (isDisabled) return null;

    if (isOpen) {
      onClose();
    } else {
      onOpen();
    }
  }, [isDisabled, isOpen, onClose, onOpen]);
  var clearOnClick = useCallback(() => {
    if (isDisabled) return null;
    setFocusedIndex(0);
    setInputValue('');
    setValue('');
  }, [isDisabled, setFocusedIndex, setInputValue, setValue]);
  var onKeyDown = useCallback(event => {
    var eventKey = normalizeEventKey(event);
    var keyMap = {
      Escape: onClose,
      ArrowDown: onContainerClick,
      Enter: onContainerClick,
      ArrowUp: onClose
    };
    var action = keyMap[eventKey];

    if (action) {
      event.preventDefault();
      event.stopPropagation();
      action(event);
    }
  }, [onContainerClick, onClose]);
  var containerProps = {
    leftIcon,
    rightIcon,
    isSearchable,
    id: valueId,
    disabled: isDisabled,
    'data-test-id': valueId + "-container",
    'data-active': isOpen,
    'data-invalid': dataAttr(isInvalid),
    'aria-expanded': isOpen,
    'aria-haspopup': 'menu',
    'aria-controls': valueId,
    'aria-invalid': isInvalid,
    tabIndex: 0,
    isClearable: isClearable,
    onClick: callAllHandlers(props.onClick, onContainerClick),
    onKeyDown: callAllHandlers(props.onKeyDown, onKeyDown),
    ref: mergeRefs(Select.selectRef, externalRef, popper.referenceRef)
  };
  var clearProps = {
    disabled: isDisabled,
    'data-test-id': valueId + "-clear-button",
    onClick: callAllHandlers(props.onClick, clearOnClick),
    value: value
  };
  return {
    containerProps,
    clearProps
  };
}
export function useSelectInputValue(props, ref) {
  if (ref === void 0) {
    ref = null;
  }

  var Select = useSelectContext();
  var {
    register
  } = useSelectDescendant();
  var {
    inputOnChange,
    isDisabled,
    placeholderProps,
    isOpen,
    openAndFocusFirstOption,
    onClose,
    isSearchable,
    valueId
  } = Select;

  if (!Select) {
    throw new Error('useSelectContext: context is undefined. Seems you forgot to wrap component within <SelectContainer>');
  }
  /** handles input onChange */


  var onChange = useCallback(event => {
    if (isSearchable) {
      /** remove any non-alphanumeric values */
      var searchValue = stripDiacritics(event.target.value).toLowerCase().replace(/\W/g, '');
      inputOnChange(searchValue);
    }
  }, [inputOnChange, isSearchable]);
  var onKeyDown = useCallback(event => {
    var eventKey = normalizeEventKey(event);
    var keyMap = {
      Escape: onClose,
      ArrowDown: openAndFocusFirstOption,
      ArrowUp: onClose
    };
    var action = keyMap[eventKey];

    if (action) {
      event.preventDefault();
      event.stopPropagation();
      action(event);
    }
  }, [openAndFocusFirstOption, onClose]);
  /** Props for value/span and input */

  var getValueProps = _extends({}, props, {
    ref: mergeRefs(ref, Select.valueRef),
    children: placeholderProps,
    hidden: isOpen && isSearchable,
    'data-test-id': valueId + "-span",
    'aria-disabled': ariaAttr(isDisabled),
    'aria-expanded': Select.isOpen
  });

  var getInputProps = _extends({}, props, {
    'aria-disabled': ariaAttr(isDisabled),
    'aria-expanded': Select.isOpen,
    'data-test-id': valueId + "-input",
    disabled: isDisabled,
    hidden: !isSearchable || !isOpen,
    placeholder: placeholderProps,
    ref: mergeRefs(register, Select.inputRef),
    role: 'search',
    value: Select.inputValue,
    readOnly: !isSearchable,
    tabIndex: 0,
    onChange: callAllHandlers(props.onChange, onChange),
    onKeyDown: callAllHandlers(props.onKeyDown, onKeyDown)
  });

  return {
    getValueProps,
    getInputProps
  };
}
/**
 * React Hook to manage a SelectList.
 *
 * The assumption here is that the `useSelect` hook is used
 * in a component higher up the tree, and its return value
 * is passed as `context` to this hook.
 */

export function useSelectList(props, ref) {
  if (ref === void 0) {
    ref = null;
  }

  var Select = useSelectContext();

  if (!Select) {
    throw new Error('useSelectContext: context is undefined. Seems you forgot to wrap component within <Select>');
  }

  var {
    filterOptions,
    focusedIndex,
    inputValue,
    onClose,
    isOpen,
    isLazy,
    lazyBehavior,
    options,
    selectRef,
    setFocusedIndex,
    listWidth
  } = Select;
  var descendants = useSelectDescendantsContext();
  /**
   * Hook that creates a keydown event handler that listens
   * to printable keyboard character press
   */

  var createTypeaheadHandler = useShortcut({
    preventDefault: event => {
      var isSelectItem = isTargetOptionItem(event.target);
      return event.key !== ' ' && isSelectItem;
    }
  });
  var onKeyDown = useCallback(event => {
    var eventKey = normalizeEventKey(event);
    var keyMap = {
      Tab: event => event.preventDefault(),
      Escape: onClose,
      ArrowDown: () => {
        var next = descendants.nextEnabled(focusedIndex);
        if (next) setFocusedIndex(next.index);
      },
      ArrowUp: () => {
        var prev = descendants.prevEnabled(focusedIndex);

        if (prev) {
          if (focusedIndex === 0) {
            return focus(Select.inputRef.current);
          }

          return setFocusedIndex(prev.index);
        }
      }
    };
    var fn = keyMap[eventKey];

    if (fn) {
      event.preventDefault();
      fn(event);
      return;
    }
    /**
     * Typeahead: Based on current character pressed,
     * find the next item to be selected
     */


    var onTypeahead = createTypeaheadHandler(character => {
      var nextItem = getNextItemFromSearch(descendants.values(), character, item => {
        var _item$node$textConten, _item$node;

        return (_item$node$textConten = item == null ? void 0 : (_item$node = item.node) == null ? void 0 : _item$node.textContent) != null ? _item$node$textConten : '';
      }, descendants.item(focusedIndex));

      if (nextItem) {
        var index = descendants.indexOf(nextItem.node);
        setFocusedIndex(index);
      }
    });

    if (isTargetOptionItem(event.target)) {
      onTypeahead(event);
    }
  }, [Select.inputRef, createTypeaheadHandler, descendants, focusedIndex, onClose, setFocusedIndex]);
  var hasBeenOpened = useRef(false);

  if (isOpen) {
    hasBeenOpened.current = true;
  }

  var shouldRenderChildren = determineLazyBehavior({
    hasBeenSelected: hasBeenOpened.current,
    isLazy,
    lazyBehavior,
    isSelected: isOpen
  });
  var getOptionList = useCallback(() => {
    return inputValue ? filterOptions : options;
  }, [filterOptions, options, inputValue]);
  return _extends({}, props, {
    'aria-orientation': 'vertical',
    'data-test-id': Select.listId,
    handleOptionClick: Select.setValue,
    id: Select.listId,
    options: shouldRenderChildren ? getOptionList() : null,
    onKeyDown: callAllHandlers(props.onKeyDown, onKeyDown),
    ref: mergeRefs(selectRef, ref),
    role: 'menu',
    style: _extends({}, props.style, {
      transformOrigin: 'var(--popper-transform-origin)'
    }),
    tabIndex: -1,
    width: listWidth
  });
}
export function useSelectPositioner(props) {
  if (props === void 0) {
    props = {};
  }

  var {
    popper,
    isOpen
  } = useSelectContext();
  return _extends({}, props, {
    ref: popper.popperRef,
    style: {
      visibility: isOpen ? 'visible' : 'hidden'
    }
  });
}
export function useSelectOption(props, externalRef) {
  if (externalRef === void 0) {
    externalRef = null;
  }

  var {
    id,
    onMouseEnter: onMouseEnterProp,
    onMouseMove: onMouseMoveProp,
    onMouseLeave: onMouseLeaveProp,
    onClick: onClickProp,
    isFocusable
  } = props,
      htmlProps = _objectWithoutPropertiesLoose(props, _excluded);

  var Select = useSelectContext();
  var {
    setFocusedIndex,
    focusedIndex,
    closeOnSelect,
    onClose,
    selectRef,
    isOpen
  } = Select;
  var ref = useRef(null); // NOTE: causing mismatch of ids: https://github.com/chakra-ui/chakra-ui/issues/3020
  // const id = `select-option-${useId()}`

  /**
   * Register the SelectOption's node into the domContext
   */

  var {
    index,
    register
  } = useSelectDescendant();
  var onMouseEnter = useCallback(event => {
    onMouseEnterProp == null ? void 0 : onMouseEnterProp(event);
    setFocusedIndex(index);
  }, [setFocusedIndex, index, onMouseEnterProp]);
  var onMouseMove = useCallback(event => {
    onMouseMoveProp == null ? void 0 : onMouseMoveProp(event);

    if (document.activeElement !== ref.current) {
      onMouseEnter(event);
    }
  }, [onMouseEnter, onMouseMoveProp]);
  var onMouseLeave = useCallback(event => {
    onMouseLeaveProp == null ? void 0 : onMouseLeaveProp(event);
    setFocusedIndex(0);
  }, [setFocusedIndex, onMouseLeaveProp]);
  var onClick = useCallback(event => {
    onClickProp == null ? void 0 : onClickProp(event);
    if (!isTargetOptionItem(event.currentTarget)) return;
    /**
     * Close Select and parent Select's if `closeOnSelect` is set to `true`
     */

    if (closeOnSelect) {
      onClose();
    }
  }, [onClose, onClickProp, closeOnSelect]);
  var isFocused = index === focusedIndex;
  useUpdateEffect(() => {
    if (!isOpen) return;

    if (isFocused && ref.current) {
      focus(ref.current);
    } else if (document.activeElement !== selectRef.current) {
      var _ref$current;

      (_ref$current = ref.current) == null ? void 0 : _ref$current.focus();
    }
  }, [isFocused, !isFocusable, selectRef, isOpen]);
  var tabbable = useClickable({
    onClick,
    onMouseEnter,
    onMouseMove,
    onMouseLeave,
    isFocusable,
    clickOnEnter: true,
    ref: mergeRefs(externalRef, selectRef)
  });
  return _extends({}, htmlProps, tabbable, {
    id,
    ref: mergeRefs(register, ref, externalRef),
    role: 'option',
    tabIndex: isFocused ? index : -1
  });
}
//# sourceMappingURL=use-select.js.map